import { Button } from '@components/ui/button/Button';
import { faChevronDown, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCountries,
  useCoverageRules,
  useCreateCoverageRule,
  useDeleteCoverageRule,
  usePaymentMethods,
  useReorderCoverageRules,
  useUpdateCoverageRule,
  useBulkImportCoverageRules,
} from '../../hooks';
import { usePermissions } from '@shared/hooks/usePermissions';
import { ServiceModel } from '../../models/Courier';
import { CoverageRuleModel, CreateCoverageRulePayload } from '../../models/CoverageRule';
import { CoverageRuleModal, DeleteCoverageRuleModal, BulkImportCoverageRuleModal } from '../modals';
import { CoverageRulesTable } from './CoverageRulesTable';

interface ServicesListProps {
  services: ServiceModel[];
}

interface ServiceItemProps {
  service: ServiceModel;
  paymentMethods: any[];
  countries: any[];
}

const ServiceItem = ({ service, paymentMethods, countries }: ServiceItemProps) => {
  const { t } = useTranslation('coverageZonesModule');
  const { canAccess } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedRule, setSelectedRule] = useState<CoverageRuleModel | null>(null);

  // 🎯 Verificar permisos granulares
  const canCreate = canAccess(['tenant', 'tenant_3pl'], ['shipping.coverage.create']);
  const canEdit = canAccess(['tenant', 'tenant_3pl'], ['shipping.coverage.edit']);
  const canDelete = canAccess(['tenant', 'tenant_3pl'], ['shipping.coverage.delete']);

  // Hooks
  const { rules, isLoading, error } = useCoverageRules({
    serviceId: service.id,
    enabled: isOpen,
  });

  const createMutation = useCreateCoverageRule();
  const updateMutation = useUpdateCoverageRule();
  const deleteMutation = useDeleteCoverageRule();
  const reorderMutation = useReorderCoverageRules();
  const bulkImportMutation = useBulkImportCoverageRules();

  // Handlers
  const handleAddRule = () => {
    if (!canCreate) return;
    setModalMode('create');
    setSelectedRule(null);
    setIsModalOpen(true);
  };

  const handleEditRule = (rule: CoverageRuleModel) => {
    if (!canEdit) return;
    setModalMode('edit');
    setSelectedRule(rule);
    setIsModalOpen(true);
  };

  const handleDeleteRule = (rule: CoverageRuleModel) => {
    if (!canDelete) return;
    setSelectedRule(rule);
    setIsDeleteModalOpen(true);
  };

  const handleModalSubmit = (payload: CreateCoverageRulePayload) => {
    if (modalMode === 'create' && canCreate) {
      createMutation.mutate(
        { serviceId: service.id, payload },
        {
          onSuccess: () => {
            setIsModalOpen(false);
          },
        },
      );
    } else if (selectedRule && canEdit) {
      updateMutation.mutate(
        {
          ruleId: selectedRule.id,
          payload,
          serviceId: service.id,
        },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setSelectedRule(null);
          },
        },
      );
    }
  };

  const handleConfirmDelete = () => {
    if (selectedRule && canDelete) {
      deleteMutation.mutate(
        {
          ruleId: selectedRule.id,
          serviceId: service.id,
        },
        {
          onSuccess: () => {
            setIsDeleteModalOpen(false);
            setSelectedRule(null);
          },
        },
      );
    }
  };

  const handleReorder = (newOrder: CoverageRuleModel[]) => {
    if (!canEdit) return;
    const payload = {
      rules_order: newOrder.map((rule) => ({ id: rule.id })),
    };

    reorderMutation.mutate({
      serviceId: service.id,
      payload,
    });
  };

  const handleBulkImport = () => {
    if (!canCreate) return;
    setIsBulkImportModalOpen(true);
  };

  const handleBulkImportSubmit = (data: {
    file: File;
    countryId: number;
    typeRule: 'allow' | 'deny';
    paymentMethodId?: number | null;
    notes?: string | null;
    groupRanges: boolean;
  }) => {
    if (!canCreate) return;
    bulkImportMutation.mutate(
      {
        serviceId: service.id,
        file: data.file,
        countryId: data.countryId,
        typeRule: data.typeRule,
        paymentMethodId: data.paymentMethodId,
        notes: data.notes,
        groupRanges: data.groupRanges,
      },
      {
        onSuccess: () => {
          setIsBulkImportModalOpen(false);
        },
      },
    );
  };

  return (
    <>
      <div className="border border-border rounded-md bg-card transition-colors">
        {/* Service Header */}
        <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Código del servicio como pill */}
            <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-mono text-xs">
              {service.code}
            </span>
            {/* Descripción */}
            <span className="text-foreground font-medium truncate">{service.description}</span>
            {/* Contador de zonas cuando hay datos */}
            {isOpen && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {rules.length > 0
                    ? t('coverageRules.rulesCount', { count: rules.length })
                    : t('coverageRules.noRules')}
                </span>
              </>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)} className="transition-colors">
            {t('coverageRules.manageRules')}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`ml-2 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>

        {/* Rules Content */}
        {isOpen && (
          <div className="border-t border-border">
            <div className="p-4">
              {isLoading && (
                <div className="flex flex-col items-center justify-center py-8 space-y-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="text-sm text-muted-foreground">{t('loading.rules')}</p>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm py-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{t('error.loadServices')}</span>
                </div>
              )}

              {!isLoading && !error && (
                <div className="relative">
                  {/* Overlay de loading durante mutaciones */}
                  {(createMutation.isPending ||
                    updateMutation.isPending ||
                    deleteMutation.isPending ||
                    reorderMutation.isPending ||
                    bulkImportMutation.isPending) && (
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                      <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                        <p className="text-sm text-muted-foreground font-medium">
                          {createMutation.isPending && t('loading.creating')}
                          {updateMutation.isPending && t('loading.updating')}
                          {deleteMutation.isPending && t('loading.deleting')}
                          {reorderMutation.isPending && t('loading.reordering')}
                          {bulkImportMutation.isPending && t('bulkImport.importing')}
                        </p>
                      </div>
                    </div>
                  )}
                  <CoverageRulesTable
                    rules={rules}
                    onAdd={handleAddRule}
                    onBulkImport={handleBulkImport}
                    onEdit={handleEditRule}
                    onDelete={handleDeleteRule}
                    onReorder={handleReorder}
                    isReordering={reorderMutation.isPending}
                    paymentMethods={paymentMethods}
                    countries={countries}
                    canCreate={canCreate}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CoverageRuleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRule(null);
        }}
        onSubmit={handleModalSubmit}
        rule={selectedRule}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        mode={modalMode}
        paymentMethods={paymentMethods}
        countries={countries}
      />

      <DeleteCoverageRuleModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedRule(null);
        }}
        onConfirm={handleConfirmDelete}
        rule={selectedRule}
        isDeleting={deleteMutation.isPending}
      />

      <BulkImportCoverageRuleModal
        isOpen={isBulkImportModalOpen}
        onClose={() => {
          setIsBulkImportModalOpen(false);
        }}
        onSubmit={handleBulkImportSubmit}
        isSubmitting={bulkImportMutation.isPending}
        paymentMethods={paymentMethods}
        countries={countries}
      />
    </>
  );
};

export const ServicesList = ({ services }: ServicesListProps) => {
  const { t } = useTranslation('coverageZonesModule');

  // Cargar payment methods una sola vez para toda la lista
  const { paymentMethods } = usePaymentMethods();
  // Cargar países una sola vez para toda la lista
  const { countries } = useCountries();

  if (services.length === 0) {
    return <div className="text-center py-6 text-sm text-muted-foreground">{t('courier.noServices')}</div>;
  }

  return (
    <div className="space-y-3">
      {services.map((service) => (
        <ServiceItem key={service.id} service={service} paymentMethods={paymentMethods} countries={countries} />
      ))}
    </div>
  );
};
